import { ScrollArea } from '@mantine/core';
import { FamilyUnit } from '@zorro/clients';
import { getQueryValue, parseMaybeEnumValue } from '@zorro/common-utils';
import { Modal } from '@zorro/design-system/v2/Modal';
import { MODAL_ZINDEX } from '@zorro/types';
import { Box, Stack, Text } from '@zorro/web/design-system';
import { FlowIdProvider, useAppRouter } from '@zorro/web/ui-utils';
import { useRef, useState } from 'react';
import { $enum } from 'ts-enum-util';

import { useAnalytics } from '../../Analytics';
import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { ModalFooter } from '../../ModalComponents';
import { ManualSubmissionDentalPlanStep } from '../ManualSubmissionDentalPlanStep';
import { ManualSubmissionHouseholdInformationStep } from '../ManualSubmissionHouseholdInformationStep';
import { ManualSubmissionMedicalPlanStep } from '../ManualSubmissionMedicalPlanStep';
import { ManualSubmissionReviewStep } from '../ManualSubmissionReviewStep';
import {
  ManualSubmissionStepType,
  getManualSubmissionRoute,
} from '../ManualSubmissionStep';
import { ManualSubmissionVisionPlanStep } from '../ManualSubmissionVisionPlanStep';

type Props = {
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  onClose: () => void;
  isOpen: boolean;
  isDon: boolean;
};

export const ManualSubmissionModal = ({
  onboardingPeriodId,
  employerId,
  employeeId,
  onClose,
  isOpen,
  isDon,
}: Props) => {
  const { router, navigateToRoute } = useAppRouter();
  const { sendEvent } = useAnalytics();
  const viewportRef = useRef<HTMLDivElement>(null);

  const setMedicalPlans = useEnrollmentAllowanceStore(
    (state) => state.setMedicalPlans
  );
  const setDentalPlans = useEnrollmentAllowanceStore(
    (state) => state.setDentalPlans
  );
  const setVisionPlans = useEnrollmentAllowanceStore(
    (state) => state.setVisionPlans
  );
  const isDirtyInStore = useEnrollmentAllowanceStore((state) => state.isDirty);
  const setIsDirty = useEnrollmentAllowanceStore((state) => state.setIsDirty);
  const setFamilyUnit = useEnrollmentAllowanceStore(
    (state) => state.setFamilyUnit
  );
  const setNumberOfDependents = useEnrollmentAllowanceStore(
    (state) => state.setNumberOfDependents
  );

  const step =
    parseMaybeEnumValue(
      ManualSubmissionStepType,
      getQueryValue(router.query.step)
    ) ?? ManualSubmissionStepType.HOUSEHOLD_INFORMATION;

  const isFlowDirty =
    isDirtyInStore || step !== ManualSubmissionStepType.HOUSEHOLD_INFORMATION;

  const [isConfirmDismissOpen, setIsConfirmDismissOpen] = useState(false);

  const handleReset = () => {
    setIsDirty(false);
    setMedicalPlans([]);
    setDentalPlans([]);
    setVisionPlans([]);
    setFamilyUnit(FamilyUnit.EMPLOYEE_ONLY);
    setNumberOfDependents(null);
  };

  const closeAndReset = async (wasConfirmed: boolean) => {
    await sendEvent('manual_election_abandoned', {
      onboarding_period_id: onboardingPeriodId,
      step,
      was_confirmed: wasConfirmed,
    });
    onClose();
    handleReset();
    navigateToRoute(
      getManualSubmissionRoute(isDon),
      [
        {
          onboardingPeriodId,
          employerId,
          employeeId,
        },
      ],
      { step: ManualSubmissionStepType.HOUSEHOLD_INFORMATION }
    );
  };

  const handleDismiss = async () => {
    await sendEvent('manual_election_dismiss_click', {
      onboarding_period_id: onboardingPeriodId,
      step,
      did_edit_fields: isFlowDirty,
    });

    if (isFlowDirty) {
      setIsConfirmDismissOpen(true);
    } else {
      await closeAndReset(false);
    }
  };

  const handleKeepEditing = async () => {
    setIsConfirmDismissOpen(false);
    await sendEvent('manual_election_quit_cancelled', {
      onboarding_period_id: onboardingPeriodId,
      step,
    });
  };

  const handleSubmit = () =>
    viewportRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <Modal
        onClose={handleKeepEditing}
        isOpen={isConfirmDismissOpen}
        title="Quit before submission?"
        size="md"
        zIndex={MODAL_ZINDEX + 1}
        footer={
          <ModalFooter
            submitText="Quit"
            submitVariant="negative"
            onClose={handleKeepEditing}
            onSubmit={() => closeAndReset(true)}
            secondaryText="Keep editing"
          />
        }
      >
        <Stack>
          <Text size="sm">Changes on this step will be lost</Text>
        </Stack>
      </Modal>
      <Modal
        onClose={handleDismiss}
        isOpen={isOpen}
        size="83rem"
        shouldCloseOnClickOutside={false}
        shouldCloseOnEscape={false}
      >
        <ScrollArea
          styles={{ viewport: { overflowX: 'hidden' } }}
          viewportRef={viewportRef}
          h="calc(100vh - 14rem)"
          offsetScrollbars="y"
          type="never"
        >
          <FlowIdProvider isOpen={isOpen}>
            {$enum.mapValue(step).with({
              [ManualSubmissionStepType.HOUSEHOLD_INFORMATION]: (
                <ManualSubmissionHouseholdInformationStep
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  onSubmit={handleSubmit}
                  isDon={isDon}
                />
              ),
              [ManualSubmissionStepType.MEDICAL_PLAN]: (
                <ManualSubmissionMedicalPlanStep
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  onSubmit={handleSubmit}
                  isDon={isDon}
                />
              ),
              [ManualSubmissionStepType.DENTAL]: (
                <ManualSubmissionDentalPlanStep
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  onSubmit={handleSubmit}
                  isDon={isDon}
                />
              ),
              [ManualSubmissionStepType.VISION]: (
                <ManualSubmissionVisionPlanStep
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  onSubmit={handleSubmit}
                  isDon={isDon}
                />
              ),
              [ManualSubmissionStepType.REVIEW]: (
                <ManualSubmissionReviewStep
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  onClose={() => {
                    handleReset();
                    onClose();
                  }}
                  isDon={isDon}
                />
              ),
            })}
          </FlowIdProvider>
        </ScrollArea>
        <Box id="manual-submission-footer" m="0 -2.5rem -1rem -1.75rem" />
      </Modal>
    </>
  );
};
